import React, { useMemo } from "react";
import SpamSafetyCheck from "./SpamSafetyCheck";

// isolate the campaign HTML in its own document so its styles never leak into
// the app and the preview stays fully contained inside its box.
const frameDoc = (h) =>
  '<!doctype html><html><head><meta charset="utf-8"/>' +
  '<meta name="viewport" content="width=device-width, initial-scale=1"/>' +
  '<style>html,body{margin:0;padding:20px;background:#fff;color:#111;' +
  'font-family:Arial,Helvetica,sans-serif;line-height:1.5;}img{max-width:100%;height:auto;}</style>' +
  '</head><body>' + (h || "<p style='color:#8a90a6'>No content created yet.</p>") + '</body></html>';

export default function ReviewStep({
  form,
  selectedCount,
  goToStep
}) {
  const tracking = form.tracking || {};
  const previewDoc = useMemo(() => frameDoc(form.html), [form.html]);

  return (
    <div className="sx-review-layout">
      <div className="sx-review-header">
        <h2>Review & Confirm</h2>

        <p>
          Review all campaign settings before sending or scheduling.
          You can click any section below to edit it.
        </p>
      </div>

      <SpamSafetyCheck form={form} />

      <div className="sx-review-grid">
        <div
          className="sx-review-card clickable"
          onClick={() => goToStep(0)}
        >
          <div className="sx-review-card-head">
            <h3>Sender</h3>
            <span>Edit</span>
          </div>

          <div className="sx-review-content">
            <p>
              <strong>Campaign:</strong> {form.name || "-"}
            </p>

            <p>
              <strong>Subject:</strong> {form.subject || "-"}
            </p>

            <p>
              <strong>Preview Text:</strong>{" "}
              {form.previewText || "Not provided"}
            </p>

            <div className="sx-review-senders-row">
              <strong>Senders:</strong>{" "}
              {Array.isArray(form.selectedSenders) && form.selectedSenders.length ? (
                form.selectedSenders.map((s, i) => (
                  <span key={i} className="campaign-sender-pill">
                    {(s.name || s.senderName || "Sender")} &lt;
                    {s.email || s.senderEmail || s.fromEmail || "-"}&gt;
                  </span>
                ))
              ) : (
                <span>
                  {form.fromName || "Sender"} &lt;{form.fromEmail || "-"}&gt;
                </span>
              )}
            </div>
          </div>
        </div>

        <div
          className="sx-review-card clickable"
          onClick={() => goToStep(1)}
        >
          <div className="sx-review-card-head">
            <h3>Editor Type</h3>
            <span>Edit</span>
          </div>

          <div className="sx-review-content">
            <p>
              <strong>Selected Editor:</strong>{" "}
              {form.editorType === "drag"
                ? "Drag & Drop"
                : form.editorType === "ai"
                ? "AI Editor"
                : "HTML Editor"}
            </p>

            <p>
              <strong>Template:</strong>{" "}
              {form.template || "Not selected"}
            </p>
          </div>
        </div>

        <div
          className="sx-review-card clickable"
          onClick={() => goToStep(3)}
        >
          <div className="sx-review-card-head">
            <h3>Content</h3>
            <span>Edit</span>
          </div>

          <div className="sx-review-content">
            <div className="sx-content-snippet">
              {form.html
                ? form.html.replace(/<[^>]+>/g, "").slice(0, 250)
                : "No content created"}
            </div>
          </div>
        </div>

        <div
          className="sx-review-card clickable"
          onClick={() => goToStep(4)}
        >
          <div className="sx-review-card-head">
            <h3>Recipients</h3>
            <span>Edit</span>
          </div>

          <div className="sx-review-content">
            <p>
              <strong>Mode:</strong>{" "}
              {form.recipientMode === "all"
                ? "All Active Contacts"
                : "Custom Selection"}
            </p>

            <p>
              <strong>Total Recipients:</strong>{" "}
              {selectedCount}
            </p>

            <p>
              <strong>Lists:</strong>{" "}
              {form.selectedLists?.length || 0}
            </p>

            <p>
              <strong>Tags:</strong>{" "}
              {form.selectedTags?.length || 0}
            </p>

            <p>
              <strong>Contacts:</strong>{" "}
              {form.selectedContacts?.length || 0}
            </p>
          </div>
        </div>

        <div
          className="sx-review-card clickable"
          onClick={() => goToStep(6)}
        >
          <div className="sx-review-card-head">
            <h3>Schedule</h3>
            <span>Edit</span>
          </div>

          <div className="sx-review-content">
            <p>
              <strong>Mode:</strong>{" "}
              {form.scheduleType === "later"
                ? "Schedule Later"
                : "Send Immediately"}
            </p>

            {form.scheduleType === "later" && (
              <>
                <p>
                  <strong>Date:</strong>{" "}
                  {form.scheduledDate || "-"}
                </p>

                <p>
                  <strong>Time:</strong>{" "}
                  {form.scheduledTime || "-"}
                </p>

                <p>
                  <strong>Timezone:</strong>{" "}
                  {form.timezone || "-"}
                </p>
              </>
            )}
          </div>
        </div>

        <div
          className="sx-review-card clickable"
          onClick={() => goToStep(6)}
        >
          <div className="sx-review-card-head">
            <h3>Tracking Configuration</h3>
            <span>Edit</span>
          </div>

          <div className="sx-review-content">
            <p>
              Open Tracking:{" "}
              <strong>
                {tracking.openTracking ? "Enabled" : "Disabled"}
              </strong>
            </p>

            <p>
              Click Tracking:{" "}
              <strong>
                {tracking.clickTracking ? "Enabled" : "Disabled"}
              </strong>
            </p>

            <p>
              Google Analytics:{" "}
              <strong>
                {tracking.googleAnalytics ? "Enabled" : "Disabled"}
              </strong>
            </p>

            <p>
              Unsubscribe Tracking:{" "}
              <strong>
                {tracking.unsubscribeTracking
                  ? "Enabled"
                  : "Disabled"}
              </strong>
            </p>
          </div>
        </div>
      </div>

      <div className="sx-final-preview">
        <div className="sx-final-preview-head">
          <h3>Email Preview</h3>

          <button
            type="button"
            onClick={() => goToStep(3)}
          >
            Edit Content
          </button>
        </div>

        <div className="sx-final-email-stage">
          <iframe
            title="Email preview"
            className="sx-final-email-iframe"
            sandbox="allow-same-origin"
            srcDoc={previewDoc}
          />
        </div>
      </div>
    </div>
  );
}
